import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import Pagination from './common/Pagination';
import LoadingSpinner from './common/LoadingSpinner';
import { AnimatedBackground, ModernCard } from './ui/modern';

const AuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    entityType: '',
    action: '',
    search: '',
    startDate: '',
    endDate: ''
  });
  const [stats, setStats] = useState(null);
  const [expandedLog, setExpandedLog] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });
      
      if (filters.entityType) params.append('entityType', filters.entityType);
      if (filters.action) params.append('action', filters.action);
      if (filters.search) params.append('search', filters.search);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const res = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(res.data.data);
      setPagination(prev => ({
        ...prev,
        total: res.data.pagination.total,
        totalPages: res.data.pagination.totalPages
      }));
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/audit-logs/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const actionBadge = (action) => {
    const styles = {
      CREATE: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      UPDATE: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      DELETE: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[action] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}>
        {action}
      </span>
    );
  };

  const formatChanges = (changes) => {
    if (!changes) return null;
    return (
      <div className="text-xs space-y-1 mt-2 text-gray-700 dark:text-gray-300">
        {Object.entries(changes).map(([field, { before, after }]) => (
          <div key={field} className="flex flex-wrap gap-2">
            <span className="font-medium">{field}:</span>
            <span className="text-red-600 dark:text-red-400 line-through">{String(before ?? 'null')}</span>
            <span>→</span>
            <span className="text-green-600 dark:text-green-400">{String(after ?? 'null')}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-6">
      <AnimatedBackground />
      
      <div className="relative z-10 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <ModernCard>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <span className="text-3xl">📋</span> Audit Log
          </h1>
          <p className="text-gray-600 dark:text-slate-400 mt-1">Track all system changes and user actions</p>
        </ModernCard>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <ModernCard className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-500/20">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.byAction?.CREATE || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Created</div>
            </ModernCard>
            <ModernCard className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-500/20">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.byAction?.UPDATE || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Updated</div>
            </ModernCard>
            <ModernCard className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-500/20">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.byAction?.DELETE || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Deleted</div>
            </ModernCard>
            <ModernCard>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{pagination.total}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Entries</div>
            </ModernCard>
          </div>
        )}

        {/* Filters */}
        <ModernCard>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <input
              type="text"
              placeholder="Search..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={filters.entityType}
              onChange={(e) => handleFilterChange('entityType', e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">All Entity Types</option>
              <option value="Company">Company</option>
              <option value="Branch">Branch</option>
              <option value="User">User</option>
              <option value="Task">Task</option>
              <option value="BranchUser">Branch User</option>
              <option value="BranchDevice">Branch Device</option>
            </select>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">All Actions</option>
              <option value="CREATE">Created</option>
              <option value="UPDATE">Updated</option>
              <option value="DELETE">Deleted</option>
            </select>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="Start Date"
            />
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="End Date"
            />
          </div>
        </ModernCard>

        {/* Table */}
        <ModernCard>
          {loading ? (
            <div className="p-8">
              <LoadingSpinner text="Loading audit logs..." />
            </div>
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-white/10">
                    <th className="text-left p-3 text-sm font-medium text-gray-600 dark:text-slate-400">Timestamp</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600 dark:text-slate-400">User</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600 dark:text-slate-400">Action</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600 dark:text-slate-400">Entity</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600 dark:text-slate-400">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <React.Fragment key={log.id}>
                      <tr 
                        className="border-t border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer"
                        onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                      >
                        <td className="p-3 text-sm text-gray-600 dark:text-gray-400">
                          {new Date(log.createdAt).toLocaleString()}
                        </td>
                        <td className="p-3 text-sm text-gray-900 dark:text-white">
                          {log.performedByName || 'System'}
                        </td>
                        <td className="p-3">
                          {actionBadge(log.action)}
                        </td>
                        <td className="p-3 text-sm">
                          <div className="font-medium text-gray-900 dark:text-white">{log.entityType}</div>
                          {log.entityName && (
                            <div className="text-gray-500 dark:text-gray-400 text-xs">{log.entityName}</div>
                          )}
                        </td>
                        <td className="p-3 text-sm text-gray-500 dark:text-gray-400">
                          {log.changes ? (
                            <span className="text-blue-600 dark:text-blue-400">
                              {Object.keys(log.changes).length} field(s) changed
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                      {expandedLog === log.id && log.changes && (
                        <tr className="bg-gray-50 dark:bg-white/5">
                          <td colSpan={5} className="p-4 border-t border-gray-200 dark:border-white/10">
                            <div className="text-sm font-medium mb-2 text-gray-900 dark:text-white">Changes:</div>
                            {formatChanges(log.changes)}
                            {log.ipAddress && (
                              <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                                IP: {log.ipAddress}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {logs.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-gray-500 dark:text-gray-400">
                        No audit logs found
                      </td>
                    </tr>
                  )}
                </tbody>
                </table>
              </div>
              <div className="md:hidden p-4 space-y-3">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="border border-gray-200 dark:border-white/10 rounded-xl p-4 space-y-2"
                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(log.createdAt).toLocaleString()}
                        </div>
                        <div className="text-gray-900 dark:text-white font-semibold">
                          {log.performedByName || 'System'}
                        </div>
                      </div>
                      <div>{actionBadge(log.action)}</div>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="text-gray-900 dark:text-white font-medium">{log.entityType}</span>
                      {log.entityName ? ` · ${log.entityName}` : ''}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {log.changes ? (
                        <span className="text-blue-600 dark:text-blue-400">
                          {Object.keys(log.changes).length} field(s) changed
                        </span>
                      ) : (
                        '-'
                      )}
                    </div>
                    {expandedLog === log.id && log.changes && (
                      <div className="pt-2 border-t border-gray-200 dark:border-white/10">
                        <div className="text-sm font-medium mb-2 text-gray-900 dark:text-white">Changes:</div>
                        {formatChanges(log.changes)}
                        {log.ipAddress && (
                          <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                            IP: {log.ipAddress}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {logs.length === 0 && (
                  <div className="text-center text-gray-500 dark:text-gray-400">
                    No audit logs found
                  </div>
                )}
              </div>
            </>
          )}
        </ModernCard>

        {/* Pagination */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default AuditLogPage;

